// frontend/src/pages/StudentProfilePage.js (New Container Page)
import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button, Card, Collapse, Tabs, Spin, Typography, Space, Select, Descriptions, Avatar, FloatButton } from 'antd';
import { ArrowLeftOutlined, EditOutlined, UserOutlined, FileTextOutlined, CalendarOutlined, UpOutlined, MessageOutlined } from '@ant-design/icons';
import { getStudentById } from '../services/studentService';
import { getAttendanceReport } from '../services/attendanceService';
import dayjs from 'dayjs';
import StudentReportCardView from './StudentReportCardView';
import useResponsive from '../hooks/useResponsive';
import { useNotification } from '../context/NotificationContext';
import { useAuth } from '../context/AuthContext';
import { getTutorGroup } from '../services/assignmentService';
import TutorFeedbackTab from '../components/TutorFeedbackTab';
import api from '../services/api';
import femaleAvatar from '../assets/female.png';
import maleAvatar from '../assets/male.png';

const { Title, Text } = Typography;
const { TabPane } = Tabs;
const { Panel } = Collapse;

const StudentProfilePage = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const { isMobile } = useResponsive();
    const { message: notify } = useNotification();
    const { user } = useAuth();

    const [studentData, setStudentData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [settings, setSettings] = useState(null);
    const [isClassTeacher, setIsClassTeacher] = useState(false);

    useEffect(() => {
        const fetchSettings = async () => {
            try {
                const response = await api.get('/settings');
                setSettings(response.data);
            } catch (err) {
                console.error("Failed to load settings in StudentProfilePage:", err);
            }
        };
        fetchSettings();
    }, []);

    const isEditDisabled = user?.role === 'teacher' && settings?.allow_teacher_student_edit === false && !isClassTeacher;
    const [activeTab, setActiveTab] = useState('details');
    const [isTutor, setIsTutor] = useState(false);

    const [reportType, setReportType] = useState('monthly');
    const [reportData, setReportData] = useState(null);
    const [reportLoading, setReportLoading] = useState(false);
    const [reportError, setReportError] = useState(null);

    useEffect(() => {
        fetchData();
        checkTutorStatus();
    }, [id]);

    const checkTutorStatus = async () => {
        if (user && user.role === 'teacher') {
            try {
                const studentIds = await getTutorGroup(user.id);
                setIsTutor(studentIds.includes(parseInt(id)));
            } catch (err) {
                console.error("Check tutor status error:", err);
                setIsTutor(false);
            }
        } else {
            setIsTutor(false);
        }
    };

    const fetchData = async () => {
        setLoading(true);
        try {
            const data = await getStudentById(id);
            setStudentData(data);
            if (user && user.role === 'teacher' && data && data.class_id) {
                try {
                    const response = await api.get('/classes');
                    const classes = response.data;
                    const myClass = classes.find(c => c.id === parseInt(data.class_id) && c.class_teacher_id === user.id);
                    setIsClassTeacher(!!myClass);
                } catch (err) {
                    console.error("Error checking class teacher status:", err);
                    setIsClassTeacher(false);
                }
            }
        } catch (error) {
            notify.error('Failed to load student profile.');
        } finally {
            setLoading(false);
        }
    };

    const handleGenerateReport = async () => {
        setReportLoading(true);
        setReportError(null);
        setReportData(null);
        try {
            const data = await getAttendanceReport(id, { type: reportType });
            setReportData(data);
        } catch (err) {
            setReportError('Failed to generate report.');
            notify.error('Failed to generate report.');
        } finally {
            setReportLoading(false);
        }
    };

    const renderYesNo = (value) => (value === true || value === 'true') ? 'Yes' : 'No';

    // Get initials for avatar
    const getInitials = (name) => {
        if (!name) return 'S';
        const parts = name.trim().split(' ');
        if (parts.length >= 2) {
            return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
        }
        return name.substring(0, 2).toUpperCase();
    };

    const InfoRow = ({ label, value }) => (
        <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            padding: '12px 0',
            borderBottom: '1px solid #f0f0f0'
        }}>
            <Text style={{ color: '#666', fontSize: '14px' }}>{label}</Text>
            <Text strong style={{ fontSize: '14px', color: '#333', textAlign: 'right' }}>{value || 'N/A'}</Text>
        </div>
    );

    if (loading) {
        return (
            <div style={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                height: '100vh',
                background: '#f5f5f5'
            }}>
                <Spin size="large" />
            </div>
        );
    }

    if (!studentData) {
        return <Title level={3}>Student data not found.</Title>;
    }

    const AttendanceReportTab = () => (
        <Card title="Attendance Report">
            <Space>
                <Select value={reportType} onChange={setReportType} style={{ width: 120 }}>
                    <Select.Option value="weekly">Weekly</Select.Option>
                    <Select.Option value="monthly">Monthly</Select.Option>
                    <Select.Option value="yearly">Yearly</Select.Option>
                </Select>
                <Button onClick={handleGenerateReport} loading={reportLoading}>
                    Generate Report
                </Button>
            </Space>
            {reportError && <p style={{ color: 'red', marginTop: '10px' }}>{reportError}</p>}
            {reportData && (
                <div style={{ marginTop: 20 }}>
                    <Descriptions title="Report Summary" bordered column={1} size="small">
                        <Descriptions.Item label="Total Days Present">{reportData.presentDays}</Descriptions.Item>
                        <Descriptions.Item label="Total Days Absent">{reportData.absentDays}</Descriptions.Item>
                        <Descriptions.Item label="Attendance Percentage">{reportData.attendancePercentage}%</Descriptions.Item>
                    </Descriptions>
                </div>
            )}
        </Card>
    );
    const getAvatarSrc = (item) => {
        return item.profile_picture_url
            ? `${process.env.REACT_APP_API_URL || ''}${item.profile_picture_url}`
            : (item.gender && item.gender.toLowerCase() === 'female' ? femaleAvatar : maleAvatar);
    };
    const StudentDetailsTab = () => (

        <div style={{
            minHeight: '100vh',
            background: '#f5f5f5',
            padding: isMobile ? '20px 16px' : '40px 20px',
            marginLeft: isMobile ? '-24px' : '-24px',
            marginRight: isMobile ? '-24px' : '-24px',
            marginTop: '-24px'
        }}>
            <div style={{
                maxWidth: '600px',
                margin: '0 auto'
            }}>
                {/* Profile Header */}
                <div style={{
                    textAlign: 'center',
                    marginBottom: '30px',
                    paddingTop: '20px'
                }}>
                    <img
                        src={getAvatarSrc(studentData)}
                        alt="Profile"
                        style={{
                            width: 120,
                            height: 120,
                            objectFit: 'cover',
                            borderRadius: '50%',
                            border: '2px solid #fff',
                            boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
                        }}
                    />
                    <Title level={2} style={{
                        margin: '0 0 8px 0',
                        fontSize: '28px',
                        fontWeight: '600',
                        color: '#2c3e50'
                    }}>
                        {studentData.student_name}
                    </Title>
                    <Text style={{
                        fontSize: '16px',
                        color: '#7f8c8d'
                    }}>
                        Student ID: {studentData.admission_no}
                    </Text>
                </div>

                {/* Accordion Sections */}
                <Collapse
                    defaultActiveKey={['1']}
                    expandIconPosition="end"
                    expandIcon={({ isActive }) => <UpOutlined rotate={isActive ? 0 : 180} />}
                    style={{
                        background: 'transparent',
                        border: 'none'
                    }}
                >
                    {/* Enrollment & Basic Details */}
                    <Panel
                        header={
                            <Text strong style={{ fontSize: '16px', color: '#2c3e50' }}>
                                Enrollment & Basic Details
                            </Text>
                        }
                        key="1"
                        style={{
                            marginBottom: '16px',
                            background: 'white',
                            borderRadius: '12px',
                            border: 'none',
                            overflow: 'hidden',
                            boxShadow: '0 2px 8px rgba(0,0,0,0.06)'
                        }}
                    >
                        <div style={{ padding: '0 8px' }}>
                            <InfoRow
                                label="Class/Grade"
                                value={`${studentData.class_name || 'Unassigned'}${studentData.roll_no ? ` - Roll No ${studentData.roll_no}` : ''}`}
                            />
                            <InfoRow
                                label="Date of Birth"
                                value={studentData.dob ? dayjs(studentData.dob).format('DD/MM/YYYY') : 'N/A'}
                            />
                            <InfoRow
                                label="Gender"
                                value={studentData.gender}
                            />
                            <InfoRow
                                label="Enrollment Date"
                                value={studentData.date_of_admission ? dayjs(studentData.date_of_admission).format('DD/MM/YYYY') : 'N/A'}
                            />
                            <InfoRow
                                label="Blood Group"
                                value={studentData.blood_group}
                            />
                            <InfoRow
                                label="Second Language"
                                value={studentData.second_language}
                            />
                            <InfoRow
                                label="Aadhar No"
                                value={studentData.aadhar_no}
                            />
                        </div>
                    </Panel>

                    {/* SSLC Grades Details */}
                    <Panel
                        header={
                            <Text strong style={{ fontSize: '16px', color: '#2c3e50' }}>
                                SSLC Grades
                            </Text>
                        }
                        key="4"
                        style={{
                            marginBottom: '16px',
                            background: 'white',
                            borderRadius: '12px',
                            border: 'none',
                            overflow: 'hidden',
                            boxShadow: '0 2px 8px rgba(0,0,0,0.06)'
                        }}
                    >
                        <div style={{ padding: '0 8px' }}>
                            <InfoRow
                                label="Maths"
                                value={studentData.sslc_maths}
                            />
                            <InfoRow
                                label="Physics"
                                value={studentData.sslc_physics}
                            />
                            <InfoRow
                                label="Chemistry"
                                value={studentData.sslc_chemistry}
                            />
                            <InfoRow
                                label="Biology"
                                value={studentData.sslc_biology}
                            />
                            <InfoRow
                                label="Social Studies"
                                value={studentData.sslc_social_studies}
                            />
                            <InfoRow
                                label="English"
                                value={studentData.sslc_english}
                            />
                        </div>
                    </Panel>

                    {/* Family & Contact Details */}
                    <Panel
                        header={
                            <Text strong style={{ fontSize: '16px', color: '#2c3e50' }}>
                                Family & Contact Details
                            </Text>
                        }
                        key="2"
                        style={{
                            marginBottom: '16px',
                            background: 'white',
                            borderRadius: '12px',
                            border: 'none',
                            overflow: 'hidden',
                            boxShadow: '0 2px 8px rgba(0,0,0,0.06)'
                        }}
                    >
                        <div style={{ padding: '0 8px' }}>
                            <InfoRow
                                label="Address"
                                value={studentData.address}
                            />
                            <InfoRow
                                label="Contact Phone"
                                value={studentData.phone_number}
                            />
                            <InfoRow
                                label="Guardian Name"
                                value={studentData.guardian_name}
                            />
                            <InfoRow
                                label="Guardian Phone"
                                value={studentData.guardian_phone_number}
                            />
                            <InfoRow
                                label="Ration Card Type"
                                value={studentData.ration_card_type}
                            />
                            <InfoRow
                                label="Father's Name"
                                value={studentData.parent_name}
                            />
                            <InfoRow
                                label="Father Alive"
                                value={renderYesNo(studentData.father_is_alive)}
                            />
                            <InfoRow
                                label="Father Occupation"
                                value={studentData.father_occupation}
                            />
                            <InfoRow
                                label="Father Qualification"
                                value={studentData.father_qualification}
                            />
                            <InfoRow
                                label="Mother's Name"
                                value={studentData.mother_name}
                            />
                            <InfoRow
                                label="Mother Alive"
                                value={renderYesNo(studentData.mother_is_alive)}
                            />
                            <InfoRow
                                label="Mother Occupation"
                                value={studentData.mother_occupation}
                            />
                            <InfoRow
                                label="Mother Qualification"
                                value={studentData.mother_qualification}
                            />
                        </div>
                    </Panel>

                    {/* Academic & Financial Details */}
                    <Panel
                        header={
                            <Text strong style={{ fontSize: '16px', color: '#2c3e50' }}>
                                Academic & Financial Details
                            </Text>
                        }
                        key="3"
                        style={{
                            marginBottom: '16px',
                            background: 'white',
                            borderRadius: '12px',
                            border: 'none',
                            overflow: 'hidden',
                            boxShadow: '0 2px 8px rgba(0,0,0,0.06)'
                        }}
                    >
                        <div style={{ padding: '0 8px' }}>
                            <InfoRow
                                label="SSLC Reg. No"
                                value={studentData.sslc_reg_no}
                            />
                            <InfoRow
                                label="SSLC School"
                                value={studentData.sslc_school}
                            />
                            <InfoRow
                                label="Religion/Caste"
                                value={studentData.religion_caste}
                            />
                            <InfoRow
                                label="SWS No"
                                value={studentData.sws_no}
                            />
                            <InfoRow
                                label="Bank Name"
                                value={studentData.bank_name}
                            />
                            <InfoRow
                                label="Bank A/C No"
                                value={studentData.bank_account_no}
                            />
                            <InfoRow
                                label="IFSC Code"
                                value={studentData.ifsc_code}
                            />
                            <InfoRow
                                label="Scholarship Received"
                                value={renderYesNo(studentData.scholarship_received)}
                            />
                        </div>
                    </Panel>
                </Collapse>


            </div>
        </div>
    );

    return (
        <>
            <div style={{ display: 'flex', alignItems: 'center', marginBottom: 16 }}>
                <Button icon={<ArrowLeftOutlined />} onClick={() => navigate(-1)}></Button>
                <div style={{ width: '10px' }}></div>
                {!isEditDisabled && (
                    <Button icon={<EditOutlined />} onClick={() => navigate(`/students/edit/${id}`)}></Button>
                )}
            </div>

            <Tabs activeKey={activeTab} onChange={setActiveTab}>
                <TabPane tab={<span><UserOutlined /> Details</span>} key="details">
                    <StudentDetailsTab />
                </TabPane>
                <TabPane tab={<span><FileTextOutlined /> Report Card</span>} key="report_card">
                    <StudentReportCardView studentId={id} />
                </TabPane>
                <TabPane tab={<span><CalendarOutlined /> Attendance</span>} key="attendance">
                    <AttendanceReportTab />
                </TabPane>
                {(user?.role === 'principal' || user?.role === 'super_admin' || isTutor) && (
                    <TabPane tab={<span><MessageOutlined /> Tutor Feedback</span>} key="tutor_feedback">
                        <TutorFeedbackTab studentId={id} isTutor={isTutor} />
                    </TabPane>
                )}
            </Tabs>
        </>
    );
};

export default StudentProfilePage;
